import { useMutation, useQueryClient } from 'react-query';
import { useFirebase } from 'components/Firebase/index.js';
import { useAlert } from 'contexts/AlertContext.jsx';
import useOrgId from 'hooks/useOrgId.js';
import { productCatalogueServerUrl } from '../../../client-config.js';

/**
 * Create a product in the Product Catalogue service.
 * Supports both org-level and feed-scoped endpoints.
 *
 * POST /api/v1/orgs/:orgId/products
 * POST /api/v1/orgs/:orgId/feeds/:feedId/products
 *
 * Body must follow essentialProductSchema (see product-catalog service):
 * { id, title, price, link, image_link, availability?, quantity? }
 */
const useCreateCatalogueProductMutation = (options = {}) => {
  const firebase = useFirebase();
  const { setAlert } = useAlert();
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options || {};

  return useMutation({
    mutationFn: async ({ product, feedId, upsert = false } = {}) => {
      if (!product || typeof product !== 'object') {
        throw new Error('Missing product payload');
      }
      const idToken = await firebase.getIdToken();
      const base = `${productCatalogueServerUrl}/api/v1/orgs/${orgId}`;
      const path = feedId && feedId !== orgId
        ? `${base}/feeds/${encodeURIComponent(feedId)}/products`
        : `${base}/products`;
      // upsert=true lets a re-added product refresh its stored image_link/price
      // instead of 409ing — the storefront image may have changed since the first
      // import. Manual catalog creation omits it so duplicate SKUs still 409.
      const url = upsert ? `${path}?upsert=true` : path;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(product),
      });

      if (response.ok) {
        return response.json();
      }

      throw {
        status: response.status,
        statusText: response.statusText,
        ...await response.json(),
      };
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(['products', orgId]);
      queryClient.invalidateQueries(['products-infinite', orgId]);
      onSuccess?.(data, variables, context);
      setAlert({ title: 'Product created' });
    },
    onError: (error) => {
      if (error?.status === 409) return;
      setAlert({ title: 'Failed to create product', variant: 'danger' });
    },
    ...rest,
  });
};

export default useCreateCatalogueProductMutation;

